// PersonPanel — the person lens's right pane in the access-control console
// (PLEX-4095): everything one user reaches in the cell and why, with bulk
// revoke of their grants and a "Grant to…" picker for what they can't reach.
// State lives in AccessConsole, because the tree also selects here.

import { grantTargets, personRows } from "./accessModel";

const resourceName = (row) => row.name || <><code>{row.id}</code> <span className="adminAcc-muted">(no record)</span></>;

const PersonPanel = ({
  resources, person, checked, onToggle, onRevokeSelected,
  grantTarget, onGrantTargetChange, onGrant, busy, chipClass,
}) => {
  if (person.loading) return <div className="loading">Looking up {person.email}…</div>;
  if (person.error) return <div className="err" role="alert">{person.error}</div>;
  if (!person.access) return null;

  const rows = personRows(resources, person.access);
  const targets = grantTargets(resources, person.access);
  const selectedCount = rows.filter((r) => r.revocable && checked.has(r.key)).length;

  return (
    <div className="adminAcc-detail adminAcc-person">
      <div className="adminEnt-eyebrow">Person</div>
      <h2 className="serif">{person.access.email}</h2>
      {person.access.plexAdmin && (
        <p className="adminAcc-note adminAcc-person__admin">
          A Plex administrator: they reach everything through the admin bypass. Below is what they
          reach without it.
        </p>
      )}

      {rows.length === 0 ? (
        <div className="empty adminAcc-person__empty">They can't reach any program or project in this cell.</div>
      ) : (
        <>
          <table className="adminAcc-table">
            <thead>
              <tr>
                <th aria-label="Select" />
                <th>Program or project</th>
                <th>Access</th>
                <th>Owner</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.key} data-key={row.key} className={checked.has(row.key) ? "is-checked" : undefined}>
                  <td className="adminAcc-check">
                    {row.revocable && (
                      <input
                        type="checkbox"
                        aria-label={`Select ${row.name || row.id}`}
                        checked={checked.has(row.key)}
                        onChange={() => onToggle(row.key)}
                        disabled={busy}
                      />
                    )}
                  </td>
                  <td>
                    <div>{resourceName(row)}</div>
                    <div className="adminAcc-muted adminAcc-small">
                      {row.kind === "program" ? "Program" : <>Project in {row.programName}</>}
                    </div>
                  </td>
                  <td><span className={`chip ${chipClass(row.chip)}`}>{row.label}</span></td>
                  <td className="adminAcc-muted">{row.ownerEmail || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="adminAcc-bulk">
            <button type="button" className="btn adminAcc-revokeSelected"
                    disabled={busy || selectedCount === 0} onClick={onRevokeSelected}>
              {busy ? "Working…" : `Revoke selected (${selectedCount})`}
            </button>
            <span className="adminAcc-muted adminAcc-small">
              Only their own grants can be revoked here. Access through a program goes with the
              program's row; owners and chats they started keep theirs.
            </span>
          </div>
        </>
      )}

      <form className="adminAcc-grant" onSubmit={(e) => { e.preventDefault(); onGrant(); }}>
        <label className="adminAcc-grant__label">
          <span>Grant to…</span>
          <select className="adminAcc-input adminAcc-grantTo" value={grantTarget || ""}
                  onChange={(e) => onGrantTargetChange(e.target.value || null)} disabled={busy}>
            <option value="">Pick a program or project they can't reach</option>
            {targets.programs.length > 0 && (
              <optgroup label="Programs (and all their projects)">
                {targets.programs.map((p) => <option key={p.id} value={`program:${p.id}`}>{p.name || p.id}</option>)}
              </optgroup>
            )}
            {targets.projects.length > 0 && (
              <optgroup label="Projects (that project only)">
                {targets.projects.map((p) => <option key={p.id} value={`project:${p.id}`}>{p.name || p.id}</option>)}
              </optgroup>
            )}
          </select>
        </label>
        <button type="submit" className="btn primary" disabled={busy || !grantTarget}>Grant</button>
      </form>
    </div>
  );
};

export default PersonPanel;
